/**
 * useSearchParams compatibility for react-router-dom v5.
 * v5 does not provide useSearchParams; this hook mirrors the v6 API
 * using useLocation and useHistory.
 */
import { useCallback, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";

type SetSearchParamsInput =
  | URLSearchParams
  | ((prev: URLSearchParams) => URLSearchParams);

interface SetSearchParamsOptions {
  replace?: boolean;
}

export function useSearchParams(): [
  URLSearchParams,
  (next: SetSearchParamsInput, options?: SetSearchParamsOptions) => void,
] {
  const history = useHistory();
  const location = useLocation();

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const setSearchParams = useCallback(
    (next: SetSearchParamsInput, options?: SetSearchParamsOptions) => {
      const nextParams =
        typeof next === "function"
          ? next(new URLSearchParams(location.search))
          : next;
      const search = nextParams.toString();
      const url = search ? `${location.pathname}?${search}` : location.pathname;
      if (options?.replace) {
        history.replace(url + location.hash);
      } else {
        history.push(url + location.hash);
      }
    },
    [history, location.pathname, location.search, location.hash]
  );

  return [searchParams, setSearchParams];
}
